import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, FormControlLabel, Switch, Typography } from "@mui/material";
import { Layer, Map, MapRef, Popup, Source } from "react-map-gl";
import type { FeatureCollection } from "geojson";
import type { Projection } from "react-map-gl";
import type { LayerProps } from "react-map-gl";
import configProvider from "config/configProvider";
import { Link } from "react-router-dom";
import { MapBounds, PointResult } from "points/models";
import { useMapWebglRecovery } from "map/useMapWebglRecovery";

/**
 * The points map on the list screen.
 *
 * Built on the same react-map-gl + clustered GeoJSON source as the vehicles map rather than a
 * second pattern, but it cannot load its data the same way. There are ~65,000 client points with
 * usable coordinates and one client alone holds 11,000, so "fetch them all and let Mapbox
 * cluster" is not available here the way it is for a few hundred vehicles.
 *
 * So the VIEWPORT is the query. Whatever the map is looking at is what gets fetched, and the same
 * bounds narrow the list beside it -- the two can never disagree about which points exist, which
 * is the whole point of filtering the list as you zoom.
 */

/** Emitted after the user stops moving, not during. Panning fires continuously. */
const MOVE_SETTLE_MS = 350;

const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };

/**
 * Layers defined here rather than reused from map/components/layers.
 *
 * Those are vehicle layers, not general ones: they hardcode `source: "vehicles"`, label from a
 * `unitNumber` property, and draw the unclustered case as a SYMBOL using an icon image called
 * "pin" that the vehicles map registers itself. Borrowed here, individual points silently fail
 * to draw -- Mapbox warns "Image 'pin' could not be loaded" and renders nothing at all.
 *
 * A circle needs no sprite, which is the right call for a point that has no icon of its own.
 */
const POINTS_SOURCE = "points";

const clusterLayer: LayerProps = {
  id: "point-clusters",
  type: "circle",
  source: POINTS_SOURCE,
  filter: ["has", "point_count"],
  paint: {
    "circle-color": "#1A73E8",
    "circle-opacity": 0.85,
    "circle-stroke-width": 2,
    "circle-stroke-color": "#ffffff",
    "circle-radius": ["step", ["get", "point_count"], 15, 10, 20, 50, 25, 100, 30, 750, 40],
  },
};

const clusterCountLayer: LayerProps = {
  id: "point-cluster-count",
  type: "symbol",
  source: POINTS_SOURCE,
  filter: ["has", "point_count"],
  layout: {
    "text-field": "{point_count_abbreviated}",
    "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
    "text-size": 12,
    "text-allow-overlap": true,
  },
  paint: { "text-color": "#ffffff" },
};

const unclusteredPointLayer: LayerProps = {
  id: "point-unclustered",
  type: "circle",
  source: POINTS_SOURCE,
  filter: ["!", ["has", "point_count"]],
  paint: {
    "circle-color": "#E8710A",
    "circle-radius": 7,
    "circle-stroke-width": 2,
    "circle-stroke-color": "#ffffff",
  },
};

const isPlottable = (p: PointResult) =>
  typeof p.latitude === "number" &&
  typeof p.longitude === "number" &&
  !(p.latitude === 0 && p.longitude === 0);

const PointsMap = ({
  points,
  onBoundsChange,
  isLoading,
  total,
  shown,
  height = "420px",
}: {
  points: PointResult[];
  onBoundsChange: (bounds: MapBounds) => void;
  isLoading?: boolean;
  /** How many points match the current filters in total. */
  total?: number;
  /** How many are actually plotted -- capped, so this can be lower than total. */
  shown?: number;
  height?: string;
}) => {
  const mapRef = useRef<MapRef>(null);
  // Rebuilds the map if its WebGL context dies; see the hook for why.
  const { mapKey, reuseMaps } = useMapWebglRecovery(mapRef);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [satellite, setSatellite] = useState(false);
  const [projection] = useState<Projection>({ name: "mercator" });
  const [hovered, setHovered] = useState<{ longitude: number; latitude: number; label: string } | null>(null);

  /**
   * The point the user clicked, kept until they dismiss it.
   *
   * Clicking a marker does NOT navigate. The viewport lives in component state rather than the
   * URL, so leaving the page and coming back loses wherever the user had zoomed to -- a misclick
   * would cost them the thing they were looking at. A pinned popup carries the link instead, so
   * going to the point is a deliberate second click.
   *
   * It has to be pinned rather than shown on hover: a link inside a hover popup cannot be
   * reached, because moving the cursor toward it leaves the marker and dismisses the popup.
   */
  const [selected, setSelected] = useState<{
    longitude: number;
    latitude: number;
    name: string;
    client: string;
    where: string;
    pointId: number | null;
  } | null>(null);

  const source: FeatureCollection = useMemo(() => {
    const plottable = (points ?? []).filter(isPlottable);
    if (plottable.length === 0) return EMPTY;

    return {
      type: "FeatureCollection",
      features: plottable.map((p) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.longitude!, p.latitude!] },
        properties: {
          label: p.name ?? "",
          client: p.clientName ?? "",
          where: [p.address, p.city, p.state].filter(Boolean).join(", "),
          // The id the points screens resolve by. Named pointId on the search result but it is
          // the ClientPointID -- see the points detail route.
          pointId: p.pointId ?? null,
        },
      })),
    };
  }, [points]);

  // Report the viewport once the map settles. Debounced because a pan fires move continuously,
  // and every emission is a query against 65,000 rows.
  /**
   * Mapbox measures its canvas once, when it mounts. This map mounts inside a Paper whose width
   * is still settling, so without an explicit resize the canvas keeps the width it saw first --
   * the map draws into part of its box, and getBounds() then reports that smaller area as the
   * viewport. The list would filter to a region the user is not looking at.
   */
  const resize = useCallback(() => {
    mapRef.current?.getMap()?.resize();
  }, []);

  useEffect(() => {
    // The container settles a frame or two after mount.
    const raf = requestAnimationFrame(resize);
    const settle = setTimeout(resize, 250);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      window.removeEventListener("resize", resize);
    };
  }, [resize]);

  const emitBounds = useCallback(() => {
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      const bounds = mapRef.current?.getMap()?.getBounds();
      if (!bounds) return;
      onBoundsChange({
        minLatitude: bounds.getSouth(),
        maxLatitude: bounds.getNorth(),
        minLongitude: bounds.getWest(),
        maxLongitude: bounds.getEast(),
      });
    }, MOVE_SETTLE_MS);
  }, [onBoundsChange]);

  const capped = typeof total === "number" && typeof shown === "number" && shown < total;

  return (
    <Box sx={{ position: "relative" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {isLoading
            ? "Loading locations..."
            : capped
            ? `Showing ${shown} of ${total} locations here — zoom in to see the rest.`
            : typeof total === "number"
            ? `${total} location${total === 1 ? "" : "s"} in view`
            : ""}
        </Typography>
        <FormControlLabel
          sx={{ display: "flex", alignItems: "center", m: 0 }}
          control={<Switch checked={satellite} onChange={(e) => setSatellite(e.target.checked)} size="small" />}
          label={<Typography variant="caption">Satellite</Typography>}
        />
      </Box>

      <Map
        key={mapKey}
        ref={mapRef}
        mapboxAccessToken={configProvider.MapboxApiKey}
        initialViewState={{ longitude: -98.5, latitude: 39.5, zoom: 3.4 }}
        // styleDiffing off: react-map-gl defaults to diffing one style into the next, and
        // the streets -> satellite-streets hop cannot be diffed -- mapbox logs "Unable to
        // perform style diff: Unimplemented: setSprite. Rebuilding the style from scratch."
        // and the rebuild leaves the map with a style but no live sources, so it stops
        // requesting tiles and paints the style's background colour. That background is
        // near-black, which is the black satellite map users report. Reload cleanly instead.
        styleDiffing={false}
        mapStyle={
          satellite
            ? "mapbox://styles/mapbox/satellite-streets-v12"
            : "mapbox://styles/mapbox/streets-v11"
        }
        projection={projection}
        style={{ width: "100%", height }}
        // Matching the vehicles map. Without reuseMaps this component mounts a SECOND Mapbox
        // instance under React's development double-mount, the duplicate style request comes back
        // 503, and the surviving instance is left with no style -- so it never requests a single
        // tile and renders an empty canvas with attribution on it, which looks like a broken
        // token rather than a remount.
        reuseMaps={reuseMaps}
        failIfMajorPerformanceCaveat={false}
        interactiveLayerIds={[unclusteredPointLayer.id as string, clusterLayer.id as string]}
        onLoad={() => {
          resize();
          emitBounds();
        }}
        onMoveEnd={emitBounds}
        onZoomEnd={emitBounds}
        onMouseMove={(e) => {
          // A pinned popup wins -- a hover preview flickering over the thing being read is worse
          // than no preview.
          if (selected) return;
          const feature = e.features?.[0];
          if (!feature || feature.layer?.id !== unclusteredPointLayer.id) {
            setHovered(null);
            return;
          }
          const [longitude, latitude] = (feature.geometry as any).coordinates;
          const props: any = feature.properties ?? {};
          setHovered({
            longitude,
            latitude,
            label: [props.label, props.client, props.where].filter(Boolean).join(" · "),
          });
        }}
        onClick={(e) => {
          const feature = e.features?.[0];
          if (!feature) {
            // Clicking empty map dismisses the popup, which is what everyone expects.
            setSelected(null);
            return;
          }

          const [longitude, latitude] = (feature.geometry as any).coordinates;

          // Clicking a cluster zooms into it, which is also how the list narrows.
          if (feature.layer?.id === clusterLayer.id) {
            const map = mapRef.current?.getMap();
            map?.easeTo({ center: [longitude, latitude], zoom: (map.getZoom() ?? 3) + 2, duration: 500 });
            setSelected(null);
            return;
          }

          if (feature.layer?.id !== unclusteredPointLayer.id) return;

          const props: any = feature.properties ?? {};
          setHovered(null);
          setSelected({
            longitude,
            latitude,
            name: props.label ?? "",
            client: props.client ?? "",
            where: props.where ?? "",
            pointId: props.pointId ?? null,
          });
        }}
      >
        {/* Effectively never cluster.

            clusterMaxZoom is the LAST zoom that still clusters, and the map opens at zoom 3.4 --
            so 3 means the opening continental view is already showing individual pins. This is the
            floor: dropping it further changes nothing unless initialViewState changes too.

            It was 12, which is city level. Someone zoomed to a whole state still saw one blob per
            port and had to keep zooming to learn whether it was two points or twenty.

            Safe because the map is viewport-driven and capped: only points inside the current
            bounds are fetched, up to MAP_POINT_CAP, so no zoom level can dump 65,000 pins onto the
            canvas -- the worst case is MAP_POINT_CAP pins spread across the country, and the map
            says so when it is holding some back. Clustering is left switched on rather than
            removed so a wider initial view, or a raised cap, still degrades sensibly. */}
        <Source id={POINTS_SOURCE} type="geojson" data={source} cluster clusterMaxZoom={3} clusterRadius={35}>
          <Layer {...clusterLayer} />
          <Layer {...clusterCountLayer} />
          <Layer {...unclusteredPointLayer} />
        </Source>

        {selected && (
          <Popup
            longitude={selected.longitude}
            latitude={selected.latitude}
            anchor="top"
            closeOnClick={false}
            onClose={() => setSelected(null)}
          >
            <Box sx={{ minWidth: 160 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selected.name || "Location"}
              </Typography>
              {selected.client && (
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                  {selected.client}
                </Typography>
              )}
              {selected.where && (
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                  {selected.where}
                </Typography>
              )}
              {selected.pointId !== null && (
                <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                  <Link to={`${selected.pointId}`}>View details</Link>
                </Typography>
              )}
            </Box>
          </Popup>
        )}

        {!selected && hovered && (
          <Popup
            longitude={hovered.longitude}
            latitude={hovered.latitude}
            closeButton={false}
            closeOnClick={false}
            anchor="top"
          >
            <Typography variant="caption">{hovered.label}</Typography>
          </Popup>
        )}
      </Map>
    </Box>
  );
};

export default PointsMap;
